/* ============================================================
 * src/screens/pending/_bundle.tsx
 *
 * Mechanical ESM transcription of the v.3 prototype's screens-pending.jsx.
 * The prototype destructured React from a global and exposed each screen
 * via Object.assign(window, ...). This file restores the same surface as
 * named ESM exports; per-component files in this directory re-export the
 * screen the route table maps to so downstream wiring tasks can edit a
 * single component without touching the bundle.
 * ============================================================ */
/* eslint-disable react-refresh/only-export-components, @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Icon, Btn, Pill, CircaTag, Panel, PageHead, Breadcrumbs, Tabs, Spark,
  StatCard, ScoreRing, HelpDot, TimerChip, AccessGate, AdminGate, CapPill,
  FreshnessBanner, AuditTrailLink, Field, Input, Textarea, Select, Toggle,
  SegmentedControl, Thumb, Avatar, EmptyState, Skeleton, useLoading,
  SkeletonStatRow, SkeletonCardGrid, SkeletonTable, SkeletonPanel, SkeletonList,
  Modal, SlideOver, ConfirmModal, Dropdown, Progress, TONES,
  useToast, useMutationWithToast,
} from '../../components/primitives';
// T318 — the mock fixture import retired. Channel-context surfaces (Encoder
// helper, Channel performance, Recent publishes, Featured channel) now
// bind to `useChannelList()`. Decorative tables backed by no public
// query family (provider spend, automation logs, batch ops, version
// history) render honest empty states until the real surfaces land.
import { useQuery, useMutation, useAction, usePaginatedQuery } from 'convex/react';
import { api } from '@convex/api';
import { useChannelList } from '../../hooks/useChannelList';
import { fmtDur, ago, money, statusLabels, fmtClock } from '../../lib/format';
import { putWithProgress, isSupported, MAX_BYTES, httpBase as resolveHttpBase } from '../../lib/uploadClient';
import { navigate } from '../../lib/nav';
import { Wordmark } from '../../layouts/Shell';
import { LineChart } from '../insights/_bundle';
import { MockBoundary, Provenance } from '../../components/Provenance';
import { TAG_KIND_LABEL, type TagKind } from '../../components/tags/TagAutocompleteInput';

// Silence unused-import lint for primitives this file may not reference.
void React; void useState; void useEffect; void useRef; void useCallback; void useMemo;
void Icon; void Btn; void Pill; void CircaTag; void Panel; void PageHead; void Breadcrumbs;
void Tabs; void Spark; void StatCard; void ScoreRing; void HelpDot; void TimerChip;
void AccessGate; void AdminGate; void CapPill; void FreshnessBanner; void AuditTrailLink;
void Field; void Input; void Textarea; void Select; void Toggle; void SegmentedControl;
void Thumb; void Avatar; void EmptyState; void Skeleton; void useLoading;
void SkeletonStatRow; void SkeletonCardGrid; void SkeletonTable; void SkeletonPanel; void SkeletonList;
void Modal; void SlideOver; void ConfirmModal; void Dropdown; void Progress; void TONES;
void useToast; void useMutationWithToast;
void navigate;

/* ============================================================
   CIRCA — Pending screens, states & shared modals (v2 gap-fill)
   ============================================================ */

/* ---------- 5.11 Encoder Setup Helper — REMOVED (NMW-457) ----------
 * The whole `/channels/:id/encoder` surface was retired: every value it
 * rendered was invented. The ffmpeg snippet embedded a fabricated ingest
 * host and a fabricated masked credential (`rtmps://ingest.circa.tv/live/
 * ••••••••1234`), the OBS/Wirecast/Navegador steps repeated the same host
 * and described a "codificador web de Circa" that does not exist, the
 * «Parámetros recomendados» table was a literal array, and «Copiar» was a
 * decorative button that toasted without touching the clipboard. No Convex
 * query supplies a per-channel ingest endpoint or an encoder profile
 * (`encoderHelper.getGoLiveConfig` / `getStreamKeysForChannel` are
 * internalQuery and use a different host). The real masked key lives on the
 * wired «Credenciales de entrada» surface (`api.channelStreamKeys
 * .listAllMasked`), reachable from Salir al aire. The route, the
 * `screens/channels/EncoderHelper.tsx` re-export and the route-table entry
 * were dropped with it. */

/* ---------- 5.14 Channel Performance ---------- */
export function ChannelPerformance({ id }: { id?: string }) {
  // T318 — wired channel context. Outcome tile counts are NOT exposed
  // by a public per-channel rollup query in this milestone; until that
  // surface lands, the rollups render '—' rather than fabricated counts.
  const rows = useChannelList();
  const real = rows?.find(x => x._id === id || x.slug === id);
  if (rows === undefined) {
    return <Skeleton h={420} />;
  }
  if (real === undefined) {
    return <EmptyState icon="alertTriangle" title="Canal no encontrado" body="No se localizó el canal solicitado." />;
  }
  const c = { id: real._id, _id: real._id, name: real.name };
  const outcomes: Array<[string, string | number, string?]> = [
    ['Pendiente', '—'],
    ['Disparado', '—'],
    ['Inserción observada', '—', 'good'],
    ['Sin inserción', '—', 'warn'],
    ['Error de tubería', '—', 'bad'],
    ['Tiempo agotado', '—', 'bad'],
    ['Desconocido', '—'],
    ['Histórico', '—'],
  ];
  return (
    <div>
      <PageHead crumbs={[{ label: 'Canales', href: '#/channels' }, { label: c.name, href: '#/channels/' + c.id }, { label: 'Rendimiento' }]}
        title="Rendimiento" description="Entrega de cortes de anuncio en las últimas 24 horas." />
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Cortes totales" value={<Provenance kind="absent" detail="No existe rollup de cortes por canal en el backend.">—</Provenance>} /><StatCard label="Completados" value={<Provenance kind="absent" detail="No existe rollup de cortes por canal en el backend.">—</Provenance>} /><StatCard label="Fallidos" value={<Provenance kind="absent" detail="No existe rollup de cortes por canal en el backend.">—</Provenance>} /><StatCard label="Canales" value={<Provenance kind="local" detail="Valor fijo en el cliente: esta vista es de un solo canal.">1</Provenance>} />
      </div>
      <Panel title="Desglose de resultados" reviewed="ok">
        <div className="circa-grid-4">
          {outcomes.map(([l, v, tone]) => <StatCard key={l} label={l} value={v === '—' ? <Provenance kind="absent" detail="No existe rollup de resultados de cortes por canal en el backend.">—</Provenance> : v} tone={tone as any} help={l === 'Inserción observada' ? '(1) No significa que un espectador vio el anuncio. (2) Es una señal de disparo. (3) Mide la entrega, no la audiencia.' : undefined} />)}
        </div>
      </Panel>
      {/* NMW-457 — the «Tasa de inserción en el tiempo» panel was removed: it
          rendered a permanent «Sin datos» empty state because no time-bucketed
          insertion-rate series exists anywhere in convex/ (adBreaks exposes
          only count rollups: getOutcomeBreakdown, getRecentBreaks,
          getPerChannelStats). The decorative range Select in the PageHead went
          with it — its onChange was a no-op and no rangeMs flowed anywhere.
          The page now ends on «Desglose de resultados»; both circa-grid-4 rows
          are kept intact for the getPerChannelStats wiring pass. */}
    </div>
  );
}

/* ---------- 9.5 Provider Spend ---------- */
export function ProviderSpend() {
  return (
    <MockBoundary detail="No hay consulta pública de rollup de gasto por proveedor en este milestone. El selector de rango es decorativo (onChange no-op) y la pantalla muestra un estado vacío honesto.">
      <div>
        <PageHead title="Gasto por proveedor" description="Uso de API por proveedor y tendencias de costo." actions={<Select value="Últimos 30 d" onChange={() => {}} options={['Últimos 7 d', 'Últimos 30 d', 'Año actual']} />} />
        <FreshnessBanner show message="Los datos de uso provienen de resúmenes diarios y se actualizan cada pocas horas." />
        {/* T318 — no public provider-spend rollup query in this
            milestone. Until it lands, the surface renders an honest
            empty state rather than fabricated cost cards. */}
        <EmptyState
          icon="dollar"
          title="Sin datos de gasto"
          body="No hay rollups de uso de proveedores disponibles en este momento."
        />
      </div>
    </MockBoundary>
  );
}

/* ---------- 10.6 Automation Detail — T316 / A8 decomposed ---------- */
export { AutomationDetail } from '../automations/AutomationDetail';

/* ---------- 10.7 Automation Logs ---------- */
export function AutomationLogs({ id }: { id?: string }) {
  // T318 — wired automation lookup; per-execution logs render honest empty.
  const wiredAuts = useQuery(api.automations.list, {}) as
    | Array<{ _id: string; name?: string }>
    | undefined;
  const wiredA = wiredAuts?.find(x => x._id === id);
  const [open, setOpen] = useState<number | null>(null);
  if (wiredAuts === undefined) {
    return <Skeleton h={420} />;
  }
  if (wiredA === undefined) {
    return <EmptyState icon="alertTriangle" title="Automatización no encontrada" body="No se localizó la automatización solicitada." />;
  }
  const a = { id: wiredA._id, name: wiredA.name ?? '—' };
  return (
    <div>
      <PageHead crumbs={[{ label: 'Automatizaciones', href: '#/automations' }, { label: a.name, href: '#/automations/' + a.id }, { label: 'Registro' }]}
        title={'Historial: ' + a.name} actions={<Select value="Todos" onChange={() => {}} options={['Todos', 'Éxito', 'Parcial', 'Fallido']} />} />
      <Panel pad={0}>
        {/* T318 — no public per-automation execution-log query. */}
        <div style={{ padding: 24 }}>
          <EmptyState icon="bolt" title="Sin registros" body="No hay registros de ejecuciones para mostrar." />
        </div>
        {/* keep the open setter referenced */}
        {void open}
      </Panel>
    </div>
  );
}

/* ---------- 16.1 Manage Captions (subtitle review, CONTRACTS.md section 16.2; card MA-84) ----------
 * Real rows replace the simulated seed and its MockBoundary: the current run's machine tracks from
 * `mediaAnalysis.queries.getTranscript` (draft / published / withdrawn), a preview built in the browser from
 * `listTranscriptCues` (no read of the private bucket is ever signed for a browser), "Publicar"
 * (`subtitles.attachSubtitles`) and "Retirar" (`subtitles.withdrawSubtitles`) ALWAYS rendered and following
 * `access.canPublishSubtitles`: disabled with a tooltip while it has not answered or answers false, never hidden
 * (the server's own check is the only real gate). Editorial tracks from `assets.captions` are listed read-only.
 * The console's permission mirror (`lib/permissions.ts`) is not touched: its snapshot tests are frozen (rule 17).
 * `(api as any).mediaAnalysis` until a person runs codegen (rule 10; the MA-82 / MA-86 precedent). */
const captionsApi: any = (api as any).mediaAnalysis;
const SUBTITLES_TOOLTIP = 'Requiere el permiso de publicar subtítulos';
const SUBTITLE_LANGUAGE_LABELS: Record<string, string> = { es: 'Español (automático)', pt: 'Português (automático)', en: 'English (automatic)' };
const PUBLICATION_WORDS: Record<string, { label: string; tone: string }> = {
  draft: { label: 'Borrador', tone: 'neutral' },
  approved: { label: 'Aprobada', tone: 'info' },
  published: { label: 'Publicada', tone: 'green' },
  withdrawn: { label: 'Retirada', tone: 'warn' },
};
type MachineTrack = { language: string; cueCount: number; purpose: string; published: boolean };
type TranscriptRows = { runId: string; publication: string | null; subtitleTracks: MachineTrack[] } | null | undefined;
type EditorialTrack = { url: string; format: string; language: string; isDefault?: boolean; label?: string; isAutoGenerated?: boolean };

function vttTimestamp(ms: number): string {
  const whole = Math.max(0, Math.floor(ms));
  const millis = whole % 1000;
  return `${fmtClock(whole - millis)}.${String(millis).padStart(3, '0')}`;
}

/** The WebVTT of a run's cues, built in the browser (the same cues as the private file; section 16.2). */
function cuesAsWebVtt(cues: ReadonlyArray<{ startMs: number; endMs: number; text: string }>): string {
  const blocks = cues.map((cue, i) => `${i + 1}\n${vttTimestamp(cue.startMs)} --> ${vttTimestamp(cue.endMs)}\n${cue.text}`);
  return `WEBVTT\n\n${blocks.join('\n\n')}\n`;
}

function CuePreview({ runId, language }: { runId: string; language: string }) {
  const { results, status, loadMore } = usePaginatedQuery(captionsApi.queries.listTranscriptCues, { runId }, { initialNumItems: 500 });
  const cues = (results ?? []) as Array<{ _id?: string; cueIndex: number; startMs: number; endMs: number; text: string; language: string }>;
  const exhausted = status === 'Exhausted';
  const download = () => {
    const blob = new Blob([cuesAsWebVtt(cues)], { type: 'text/vtt' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${runId}-${language}.vtt`;
    link.click();
    URL.revokeObjectURL(url);
  };
  if (status === 'LoadingFirstPage') return <div style={{ padding: 16 }}><Skeleton h={120} /></div>;
  if (cues.length === 0) return <div style={{ padding: 16 }}><EmptyState icon="fileText" title="Sin líneas" body="La transcripción de este análisis no tiene líneas." /></div>;
  return (
    <div data-testid="caption-preview" style={{ padding: '8px 20px 16px', borderTop: '1px solid var(--lightest-gray)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>Vista previa construida en el navegador a partir de las líneas del análisis.</span>
        <span style={{ flex: 1 }} />
        {!exhausted && <Btn size="sm" variant="ghost" onClick={() => loadMore(500)}>Cargar más</Btn>}
        <Btn size="sm" variant="ghost" icon="download" disabled={!exhausted} title={exhausted ? undefined : 'Cargando todas las líneas…'} onClick={download}>Descargar WebVTT</Btn>
      </div>
      <div style={{ maxHeight: 260, overflowY: 'auto', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {cues.map((cue) => (
          <div key={cue._id ?? cue.cueIndex} style={{ display: 'flex', gap: 12 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--muted-slate)', whiteSpace: 'nowrap' }}>{vttTimestamp(cue.startMs)}</span>
            <span>{cue.text}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ManageCaptions({ id }: { id?: string }) {
  // T318 — wired asset context. Guard malformed ids (hand-typed URLs, mock
  // fixture ids) so they render the not-found state instead of throwing an
  // ArgumentValidationError past the error boundary.
  const validCaptionAssetId = !!id && /^[a-z0-9]{20,}$/.test(id);
  const assetArgs = validCaptionAssetId ? { assetId: id as any } : 'skip';
  const aDoc = useQuery(api.assetQueries.getAssetById, assetArgs) as { _id?: string; title?: string; captions?: EditorialTrack[] } | undefined | null;
  const status = useQuery(captionsApi.queries.getAnalysisStatus, assetArgs) as { current: { runId: string; status: string } | null } | undefined;
  const transcript = useQuery(captionsApi.queries.getTranscript, assetArgs) as TranscriptRows;
  const can = useQuery(captionsApi.access.canPublishSubtitles, validCaptionAssetId ? {} : 'skip') as { allowed: boolean } | undefined;
  const attach = useAction(captionsApi.subtitles.attachSubtitles);
  const withdraw = useAction(captionsApi.subtitles.withdrawSubtitles);
  const { toast } = useToast();
  // Hooks before any early return (Rules of Hooks).
  const [busy, setBusy] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  if (id && !validCaptionAssetId) {
    return <EmptyState icon="alertTriangle" title="Video no encontrado" body="No se localizó el video solicitado." />;
  }
  if (id && aDoc === undefined) {
    return <Skeleton h={420} />;
  }
  if (id && aDoc === null) {
    return <EmptyState icon="alertTriangle" title="Video no encontrado" body="No se localizó el video solicitado." />;
  }
  const a = { id: aDoc?._id ?? '', _id: aDoc?._id ?? '', title: aDoc?.title ?? '—' };
  const allowed = can?.allowed === true;
  const controlTooltip = allowed ? undefined : SUBTITLES_TOOLTIP;
  const currentRunId = status?.current?.runId ?? null;
  const tracks: MachineTrack[] = transcript && currentRunId && transcript.runId === currentRunId ? transcript.subtitleTracks : [];
  const publication = transcript?.publication ?? 'draft';
  const word = PUBLICATION_WORDS[publication] ?? { label: publication, tone: 'neutral' };
  const editorial: EditorialTrack[] = Array.isArray(aDoc?.captions) ? (aDoc!.captions as EditorialTrack[]) : [];
  const machineLoading = status === undefined || transcript === undefined;

  const run = async (key: string, label: string, call: () => Promise<unknown>) => {
    setBusy(key);
    try {
      await call();
      toast({ variant: 'success', message: label });
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      toast({ variant: 'error', message: /RunNotPublishable/.test(message) ? 'Este análisis ya no es el vigente: no se puede publicar.' : message });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: a.title, href: '#/library/' + a.id }, { label: 'Subtítulos' }]}
        title="Subtítulos" description={`${a.title} — revisa y publica las pistas de subtítulos.`} actions={<Btn variant="ghost" icon="upload" onClick={() => navigate('/library/captions/bulk')}>Carga masiva</Btn>} />
      <div className="circa-cols-8-4">
        <Panel title="Pistas automáticas del análisis" pad={0}>
          {machineLoading && <div style={{ padding: 16 }}><Skeleton h={120} /></div>}
          {!machineLoading && tracks.length === 0 && (
            <div data-testid="caption-tracks-empty" style={{ padding: 24 }}>
              <EmptyState icon="fileText" title="Sin pistas automáticas" body="Este video no tiene un análisis vigente con transcripción. Ejecuta el análisis desde la pestaña Analizar." />
            </div>
          )}
          {!machineLoading && tracks.map((t, i) => (
            <div key={t.language} data-testid="caption-track" data-language={t.language} data-publication={publication}
              style={{ borderBottom: i < tracks.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px' }}>
                <Pill tone="neutral" size="sm">{t.language.toUpperCase()}</Pill>
                <span style={{ flex: 1, fontSize: 14 }}>{SUBTITLE_LANGUAGE_LABELS[t.language] ?? `${t.language} (automático)`}</span>
                <Pill tone="info" size="sm">Automática</Pill>
                <Pill tone="neutral" size="sm">WebVTT</Pill>
                <span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{t.cueCount} líneas</span>
                <Pill tone={word.tone} size="sm">{word.label}</Pill>
                {t.published && <Pill tone="green" size="sm">Pública</Pill>}
                <Btn size="sm" variant="ghost" icon="eye" onClick={() => setPreview(preview === t.language ? null : t.language)}>Vista previa</Btn>
                {/* Btn forwards no data attributes: the test ids sit on a wrapper span. */}
                <span data-testid="caption-publish">
                  <Btn size="sm" variant="primary" disabled={!allowed || busy !== null} title={controlTooltip}
                    onClick={() => { if (!allowed || !currentRunId) return; void run(`publish:${t.language}`, 'Subtítulos publicados.', () => attach({ runId: currentRunId, language: t.language })); }}>Publicar</Btn>
                </span>
                <span data-testid="caption-withdraw">
                  <Btn size="sm" variant="danger" disabled={!allowed || busy !== null} title={controlTooltip}
                    onClick={() => { if (!allowed || !currentRunId) return; void run('withdraw', 'Subtítulos retirados.', () => withdraw({ runId: currentRunId })); }}>Retirar</Btn>
                </span>
              </div>
              {preview === t.language && currentRunId && <CuePreview runId={currentRunId} language={t.language} />}
            </div>
          ))}
        </Panel>
        <Panel title="Pistas editoriales" pad={0}>
          {editorial.length === 0 && (
            <div data-testid="editorial-tracks-empty" style={{ padding: 24 }}>
              <EmptyState icon="fileText" title="Sin pistas editoriales" body="El video no tiene subtítulos editoriales cargados." />
            </div>
          )}
          {editorial.map((t, i) => (
            <div key={`${t.language}-${i}`} data-testid="editorial-track" style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 20px', borderBottom: i < editorial.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <Pill tone="neutral" size="sm">{t.language.toUpperCase()}</Pill>
              <span style={{ flex: 1, fontSize: 14 }}>{t.label ?? t.language}</span>
              {t.isAutoGenerated && <Pill tone="info" size="sm">Automática</Pill>}
              <Pill tone="neutral" size="sm">{t.format}</Pill>
              {t.isDefault && <Pill tone="green" size="sm">Predeterminada</Pill>}
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

/* ---------- 16.2 Bulk Caption Upload ---------- */
export function BulkCaptions() {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  return (
    <div>
      <PageHead crumbs={[{ label: 'Contenido', href: '#/library' }, { label: 'Subtítulos en masa' }]} title="Carga masiva de subtítulos" description="Empareja archivos de subtítulos con videos por nombre o título." />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <Panel title="1 · Subir archivos">
          <div onClick={() => setStep(2)} style={{ border: '2px dashed var(--border-cool)', borderRadius: 22, padding: '40px 24px', textAlign: 'center', cursor: 'pointer' }}>
            <Icon name="uploadCloud" size={40} color="var(--muted-slate)" />
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 18, margin: '12px 0 4px' }}>Suelta varios archivos a la vez</div>
            <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-slate)' }}>Aceptamos .scc, .srt y .vtt. Intentaremos emparejar cada uno con un video.</p>
          </div>
        </Panel>
        {step === 2 && (
          <Panel title="2 · Emparejar" pad={0}>
            <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1.6fr 1fr 1fr', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
              <span>Archivo</span><span>Video sugerido</span><span>Confianza</span><span>Idioma</span></div>
            {/* T318 — file → asset matching has no public Convex
                surface in this milestone. Honest empty state. */}
            <div style={{ padding: 24 }}>
              <EmptyState
                icon="upload"
                title="Sin emparejamientos"
                body="Sube archivos para iniciar el emparejamiento con tus videos."
              />
            </div>
            <div style={{ padding: 16, display: 'flex', justifyContent: 'flex-end' }}><Btn variant="solid" decorative onClick={() => toast({ variant: 'success', message: 'Subtítulos emparejados y subidos.' })}>Confirmar emparejamiento</Btn></div>
          </Panel>
        )}
      </div>
    </div>
  );
}

/* ---------- 6.9 Edit Metadata — T316 / A1 decomposed ---------- */
// The wired EditMetadata lives at `screens/content/EditMetadata.tsx`.
// The bundle re-exports the symbol so any indirect importer that still
// points at `../pending/_bundle` keeps resolving.
export { EditMetadata } from '../content/EditMetadata';

/* ---------- 13.2 Editorial Sync ---------- */
export function EditorialSync() {
  return (
    <AdminGate>
      <PageHead title="Sincronización editorial" description="Estado en vivo del flujo de revisión editorial." />
      <FreshnessBanner show message="El estado del flujo editorial puede ir ligeramente atrasado." />
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Aprobaciones (24 h)" value="42" /><StatCard label="Aplicadas (24 h)" value="40" tone="good" /><StatCard label="Reintentando" value="2" tone="warn" /><StatCard label="Cola de envío" value="0" tone="good" />
      </div>
      <Panel title="Ejecuciones del flujo" pad={0} style={{ marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 1fr 1fr 0.8fr', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 12, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
          <span>Inicio</span><span>Correlación</span><span>Operación</span><span>Estado</span><span>Dur.</span></div>
        {/* T318 — no public editorial-flow execution list in this
            milestone; honest empty until the surface lands. */}
        <div style={{ padding: 24 }}>
          <EmptyState
            icon="refresh"
            title="Sin ejecuciones recientes"
            body="No hay ejecuciones del flujo editorial para mostrar."
          />
        </div>
      </Panel>
      <ConnectionTestTool />
    </AdminGate>
  );
}

/* ---------- 13.4 Connection test tool ---------- */
export function ConnectionTestTool() {
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(null);
  return (
    <Panel pad={20}>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
        <Icon name={open ? 'chevronD' : 'chevronR'} size={16} color="var(--muted-slate)" />
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>Probar la conexión segura</span>
      </div>
      {open && (
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--body-muted)' }}>Úsalo para verificar que tu CMS firma las solicitudes correctamente, sin exponer la ruta de producción a tráfico de prueba.</p>
          <Field label="Cuerpo de ejemplo"><Textarea value="" onChange={() => {}} rows={3} placeholder='{"event":"asset.created"}' /></Field>
          <div className="circa-grid-2-tight">
            <Field label="Firma (hex)"><Input value="" onChange={() => {}} placeholder="a8f2…" /></Field>
            <Field label="Marca de tiempo (ms)"><Input value={String(Date.now())} onChange={() => {}} /></Field>
          </div>
          {/* T322 — V3I-FR-008 fabrication-site deletion. The prior
              `Btn ... onClick={() => setResult(Math.random() > 0.4 ? 'ok' : 'bad')}`
              shape ASSERTED a verification result via Math.random() — the
              archetypal honest-state violation. Until the Category-B
              backend `integrations.verifyConnection` ships in Phase 3, the
              affordance stays visibly disabled with an honest tooltip
              ("Signature verification: backend not yet built"). `result`
              and `setResult` remain in scope for the Phase-3 wire-up; the
              success/failure surface is intentionally absent because there
              is no real result to render. */}
          <div>
            <Btn
              variant="solid"
              disabled
              decorative
              title="Signature verification: backend not yet built"
            >
              Verificar firma
            </Btn>
          </div>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--muted-slate)' }}>Las solicitudes deben firmarse dentro de 300 segundos de su llegada. Las más antiguas se rechazan para prevenir ataques de repetición.</p>
        </div>
      )}
    </Panel>
  );
}

/* ---------- 13.3 Editorial item review ---------- */
export function EditorialItemReview({ itemId }: { itemId?: string }) {
  // T318 — wired asset lookup; the canonical wired surface is at
  // src/screens/editorial/EditorialItemReview.tsx — this bundle entry
  // mirrors its wiring for the prototype-style copy.
  // Guard the query arg: a malformed id (e.g. a hand-typed/stale URL) fails
  // `v.id("assets")` and throws an ArgumentValidationError straight to the
  // error boundary. Convex ids are long lowercase-alphanumeric strings, so
  // we only query when the id is plausibly well-formed; otherwise fall
  // through to the honest "not found" state. (No backend change.)
  const validItemId = !!itemId && /^[a-z0-9]{20,}$/.test(itemId);
  const aDoc = useQuery(
    api.assetQueries.getAssetById,
    validItemId ? { assetId: itemId as any } : 'skip',
  ) as { _id?: string; title?: string; durationMs?: number; addedAtMs?: number } | undefined | null;
  const { toast } = useToast();
  const [comment, setComment] = useState('');
  if (itemId && !validItemId) {
    return <EmptyState icon="alertTriangle" title="Elemento no encontrado" body="No se localizó el elemento solicitado." />;
  }
  if (itemId && aDoc === undefined) {
    return <Skeleton h={420} />;
  }
  if (itemId && aDoc === null) {
    return <EmptyState icon="alertTriangle" title="Elemento no encontrado" body="No se localizó el elemento solicitado." />;
  }
  const a = { id: aDoc?._id ?? '', title: aDoc?.title ?? '—', durationMs: aDoc?.durationMs ?? 0, addedAt: aDoc?.addedAtMs ?? Date.now() };
  return (
    <div>
      <PageHead crumbs={[{ label: 'Bandeja editorial', href: '#/editorial-inbox' }, { label: a.title }]} title={a.title} />
      <div className="circa-cols-8-4">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Vista previa" pad={0}><div style={{ padding: 20 }}><Thumb radius={14}><div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="play" size={26} color="#fff" /></div></Thumb></div></Panel>
          <Panel title="Descripción"><p style={{ margin: 0, fontSize: 16, color: 'var(--body-muted)', lineHeight: 1.6 }}>Contenido recibido desde una fuente conectada. Revisa la descripción y los metadatos del feed antes de aceptar o rechazar.</p></Panel>
          <Panel title="Comentarios de revisión">
            <div style={{ display: 'flex', gap: 10 }}><Input value={comment} onChange={setComment} placeholder="Añade una nota…" /><Btn variant="ghost" onClick={() => { setComment(''); toast({ variant: 'success', message: 'Comentario añadido.' }); }}>Enviar</Btn></div>
          </Panel>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <Panel title="Metadatos del feed">
            {[['Fuente', 'Reuters Video'], ['Duración', fmtDur(a.durationMs)], ['Título original', a.title], ['ID original', 'rt_' + a.id], ['Recibido', ago(a.addedAt)]].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--lightest-gray)', fontSize: 13 }}><span style={{ color: 'var(--muted-slate)' }}>{k}</span><span style={{ textAlign: 'right' }}>{v}</span></div>
            ))}
          </Panel>
          <Panel title="Decisión">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Btn variant="solid" full icon="check" onClick={() => { toast({ variant: 'success', message: 'Aceptado y añadido a la biblioteca.' }); navigate('/editorial-inbox'); }}>Aceptar</Btn>
              <Btn variant="danger" full onClick={() => { toast({ variant: 'info', message: 'Elemento rechazado.' }); navigate('/editorial-inbox'); }}>Rechazar</Btn>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}

/* ---------- 14.3 Recent Publishes ledger — REMOVED (NMW-457) ----------
 * The `/publishes` surface was retired: it could never render a row. The
 * publication ledger has NO public Convex query —
 * `convex/mutations/multiPlatformPublishLedger.ts` exports only
 * internalMutation/internalQuery members and `convex/publishing.ts` exports
 * zero queries — so the table was a permanent empty state wrapped in filter
 * chrome, with dead `shown`/`outMap` locals and a detail SlideOver that had
 * no detail to show. The route, the `screens/channels/RecentPublishes.tsx`
 * re-export and the route-table entry were dropped with it. */

/* ---------- 2.8 First-run empty workspace ---------- */
export function FirstRun() {
  return (
    <div style={{ maxWidth: 760, margin: '40px auto' }}>
      <Panel pad={40}>
        <div style={{ textAlign: 'center' }}>
          <Icon name="tv" size={48} color="var(--muted-slate)" />
          <h1 style={{ margin: '16px 0 8px', fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 400 }}>Configura tu primer canal.</h1>
          <p style={{ margin: '0 auto 28px', fontSize: 18, color: 'var(--body-muted)', maxWidth: 480 }}>Un canal es una transmisión 24×7 a la que publicas contenido. Crea uno para empezar.</p>
        </div>
        <div className="circa-grid-3" style={{ marginBottom: 28 }}>
          {[['film', 'Trae tu video', 'Sube o importa contenido a tu biblioteca.'], ['calendar', 'Programa lo que sale al aire', 'Arma una parrilla de programación.'], ['radio', 'Sal al aire', 'Transmite a YouTube o a tu propio reproductor.']].map(([ic, t, d]) => (
            <div key={t} style={{ border: '1px solid var(--lightest-gray)', borderRadius: 16, padding: 18 }}>
              <Icon name={ic} size={22} color="var(--deep-green)" />
              <div style={{ fontWeight: 500, fontSize: 15, margin: '10px 0 4px' }}>{t}</div>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--muted-slate)', lineHeight: 1.45 }}>{d}</p>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <Btn variant="solid" iconRight="arrowRight" onClick={() => navigate('/channels/new')}>Crear un canal</Btn>
          <Btn variant="ghost" onClick={() => navigate('/')}>Ir al panel</Btn>
        </div>
      </Panel>
    </div>
  );
}

/* ---------- 2.9 / 2.10 Account states ---------- */
function TakeoverState({ icon, tone, title, body, primary, secondary }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, background: 'var(--snow)' }}>
      <Panel pad={32} style={{ maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <Icon name={icon} size={36} color={TONES[tone].fg} />
        <h1 style={{ margin: '16px 0 8px', fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 400 }}>{title}</h1>
        <p style={{ margin: '0 0 24px', fontSize: 15, color: 'var(--body-muted)', lineHeight: 1.5 }}>{body}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>{primary}{secondary}</div>
      </Panel>
    </div>
  );
}
export function AccountLocked() {
  return <TakeoverState icon="alertCircle" tone="bad" title="Tu cuenta está suspendida"
    body="Un administrador suspendió tu acceso. Contacta a tu administrador para restaurarlo."
    primary={<Btn variant="solid" as="a" href="mailto:lucia@canalcirca.tv">Contactar al administrador</Btn>}
    secondary={<Btn variant="ghost" onClick={() => navigate('/sign-in')}>Cerrar sesión</Btn>} />;
}
export function SessionExpired() {
  return <TakeoverState icon="clock" tone="warn" title="Tu sesión terminó"
    body="Por tu seguridad, cerramos tu sesión. Inicia sesión de nuevo para continuar."
    primary={<Btn variant="solid" iconRight="arrowRight" onClick={() => navigate('/sign-in')}>Iniciar sesión</Btn>} />;
}

/* ---------- 17.1 Operator HTML Dashboard (light, restyled) ---------- */
export function OperatorDashboard() {
  // T318 — wired channel list for the fleet panel. Headline tiles
  // remain '—' until real per-tenant rollup queries land.
  const operDashboardRows = useChannelList();
  return (
    <div style={{ minHeight: '100vh', background: 'var(--canvas)', padding: '32px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}><Wordmark size={26} /><Pill tone="neutral">Panel del operador</Pill></div>
        <Btn variant="ghost" icon="externalLink" onClick={() => navigate('/')}>Abrir aplicación</Btn>
      </div>
      <div className="circa-grid-4" style={{ marginBottom: 24 }}>
        <StatCard label="Canales al aire" value="—" /><StatCard label="Nodos listos" value="—" /><StatCard label="Alertas" value="—" /><StatCard label="Capacidad" value="—" />
      </div>
      <div className="circa-grid-2">
        <MockBoundary detail="Estado de red de demostración.">
          <Panel title="Estado de la red" pad={0}>
            {(operDashboardRows ?? []).slice(0, 6).map((c, i) => (
              <div key={c._id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: i < Math.min((operDashboardRows ?? []).length, 6) - 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
                <Pill tone={c.backendStatus === 'running' ? 'live' : c.backendStatus === 'error' ? 'bad' : 'neutral'} size="sm" dot={c.backendStatus === 'running'}>{c.backendStatus}</Pill>
                <span style={{ flex: 1, fontSize: 14 }}>{c.name}</span>
                <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>{c.uptime}%</span>
              </div>
            ))}
          </Panel>
        </MockBoundary>
        <Panel title="Tareas en segundo plano" pad={0}>
          {['Resumen diario', 'Verificación de señal', 'Retención', 'Limpieza', 'Enriquecimiento'].map((t, i) => (
            <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 20px', borderBottom: i < 4 ? '1px solid var(--lightest-gray)' : 'none' }}>
              <span style={{ flex: 1, fontSize: 14 }}>{t}</span><Pill tone="good" size="sm">OK</Pill><span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>hace {i + 1}m</span>
            </div>
          ))}
        </Panel>
      </div>
    </div>
  );
}

/* ---------- 5.18 Asset Picker modal ---------- */
export function AssetPickerModal({ open, onClose, onPick }: { open: boolean; onClose: () => void; onPick?: (id: string) => void }) {
  const [sel, setSel] = useState<string | null>(null);
  const [q, setQ] = useState('');
  // Selected tag facet keys, `${kind}::${normalizedTag}` — same identity the
  // Library filter bar uses so both surfaces speak one vocabulary.
  const [selKeys, setSelKeys] = useState<Set<string>>(new Set());

  // Facet chips read the RESILIENT vocabulary (registry ∪ live in-use tags),
  // so the bar works on pre-existing content even before the registry backfill
  // runs. `minUsage: 1` keeps zero-usage catalog entries out — they could only
  // ever yield the empty state. Subscribed only while the modal is open.
  const facetData = useQuery(
    api.assetQueries.listTagVocabulary,
    open ? { minUsage: 1, limit: 200 } : 'skip',
  ) as
    | { tags: Array<{ tag: string; kind: string; normalizedTag: string; usageCount: number }>; count: number }
    | undefined;
  const facets = facetData?.tags ?? [];
  const fkey = (f: { kind: string; normalizedTag: string }) => `${f.kind}::${f.normalizedTag}`;
  const selectedFacets = facets.filter((f) => selKeys.has(fkey(f)));
  // Each facet carries its OWN kind so "Canal: deportes" never matches
  // category:deportes.
  const tagFacets = selectedFacets.map((f) => ({ tag: f.normalizedTag, kind: f.kind }));

  // T318 — wired to api.assetQueries.getReadyAssets. Honest skeleton
  // while the list resolves; honest empty when no ready asset matches.
  // The query returns an ENVELOPE `{ assets, count }` (convex/assetQueries.ts
  // :105), not a bare array — treating it as an array crashed the whole
  // Ajustes tab with "(ready ?? []).filter is not a function".
  //
  // Text and tags COMPOSE server-side: the title search index drives the read,
  // the tag facets intersect it (`tagMatchMode: "all"` — every selected chip
  // must match). Both filters stay on the server, so the picker never shows a
  // list the backend didn't actually vouch for.
  const ready = useQuery(api.assetQueries.getReadyAssets, {
    ...(q.trim() ? { search: q.trim() } : {}),
    ...(tagFacets.length > 0 ? { tagFacets, tagMatchMode: 'all' as const } : {}),
  }) as
    | { assets: Array<{ _id: string; title?: string; durationMs?: number; assetType?: string }>; count: number }
    | undefined;
  // Backend now filters by title (search index), tags, and status/trashed, so
  // the full returned envelope is the display list — no client-side truncation.
  const list = ready?.assets ?? [];
  const tagFilterActive = tagFacets.length > 0;

  // Leave the picker as it was found: clear both filters and the selection on
  // close, so the next "Añadir contenido" starts from the full ready library.
  const close = () => { setSel(null); setQ(''); setSelKeys(new Set()); onClose(); };

  return (
    <Modal open={open} onClose={close} title="Elegir contenido" width={760}
      footer={<><Btn variant="ghost" onClick={close}>Cancelar</Btn><Btn variant="solid" disabled={!sel} onClick={() => { onPick && onPick(sel); close(); }}>Usar este contenido</Btn></>}>
      <div style={{ marginBottom: 12 }}><Input value={q} onChange={setQ} icon="search" placeholder="Buscar por título" /></div>
      {/* Tag facet chips — independent toggles, AND-combined by the server.
          "Todas" clears the set. Hidden entirely when no tag is in use, so the
          picker stays as compact as before on untagged libraries. */}
      {facets.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginRight: 4 }}>Etiquetas</span>
          <button onClick={() => setSelKeys(new Set())} style={{ border: '1px solid var(--border-cool)', cursor: 'pointer', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontFamily: 'var(--font-sans)',
            background: !tagFilterActive ? 'var(--ink-muted)' : 'transparent', color: !tagFilterActive ? '#fff' : 'var(--slate)', borderColor: !tagFilterActive ? 'var(--ink-muted)' : 'var(--border-cool)' }}>Todas</button>
          {facets.map((f) => {
            const k = fkey(f);
            const active = selKeys.has(k);
            return (
              <button key={k}
                onClick={() => setSelKeys((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; })}
                style={{ border: '1px solid var(--border-cool)', cursor: 'pointer', borderRadius: 999, padding: '5px 12px', fontSize: 12, fontFamily: 'var(--font-sans)', display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: active ? 'var(--ink-muted)' : 'transparent', color: active ? '#fff' : 'var(--slate)', borderColor: active ? 'var(--ink-muted)' : 'var(--border-cool)' }}>
                {f.kind !== 'freeform' && <span style={{ fontSize: 10, opacity: 0.65 }}>{TAG_KIND_LABEL[f.kind as TagKind] ?? f.kind}:</span>}
                {f.tag}
                <span style={{ fontSize: 10, opacity: 0.6 }}>{f.usageCount}</span>
              </button>
            );
          })}
          {tagFilterActive && (
            <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>
              {tagFacets.length} activa(s) · deben coincidir todas
            </span>
          )}
        </div>
      )}
      <div className="circa-grid-3" style={{ maxHeight: 420, overflowY: 'auto' }}>
        {ready === undefined ? (
          <SkeletonList rows={3} />
        ) : list.length === 0 ? (
          <EmptyState
            icon="film"
            title="Sin contenido listo"
            body={
              q.trim() || tagFilterActive
                ? 'Ningún video listo coincide con los filtros aplicados.'
                : 'No hay videos listos para elegir.'
            }
          />
        ) : (
          list.map((a) => (
            <div key={a._id} onClick={() => setSel(a._id)} style={{ cursor: 'pointer', border: `2px solid ${sel === a._id ? 'var(--ink-muted)' : 'transparent'}`, borderRadius: 14, overflow: 'hidden', background: 'var(--snow)' }}>
              <Thumb radius={0} label={a.assetType ?? ''} />
              <div style={{ padding: 10 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.title ?? '—'}</div>
                <span className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)' }}>{a.durationMs !== undefined ? fmtDur(a.durationMs) : '—'}</span>
              </div>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

/* ---------- 5.3 Destinations Wizard — REMOVED (NMW-233) ----------
 * The standalone channel-destination wizard was retired: a destination is a
 * channel's output config, so editing now lives in the channel's own
 * "Ajustes → Destinos" sub-tab (ChannelDestinations) — the single canonical
 * editor. The top-level /destinations page is now a read-only roster that
 * deep-links into that editor per channel. */

/* ---------- 6.6 Batch Pattern Wizard — T316 / A3 decomposed ---------- */
export { BatchPatternWizard } from '../batch/BatchPatternWizard';

/* ---------- 6.7 Replace file modal ----------
 * NMW-240: real source-file replacement. Drives the same browser → S3 → Convex
 * flow as the Upload page, but against an EXISTING asset id (preserving its
 * metadata, ad-markers, and schedule references):
 *   1. POST /api/upload/replace-url  — flip asset to "uploading", presign PUT
 *   2. PUT bytes to S3               — putWithProgress (real %)
 *   3. POST /api/upload/complete     — restart AWS Batch transcode for this id
 * The asset detail view is wired to api.assets.list (reactive), so the new
 * conversion status + progress appear automatically once this completes.
 */
type ReplacePhase = 'idle' | 'requesting' | 'uploading' | 'completing' | 'done' | 'error';

export function ReplaceFileModal({ open, onClose, assetId }: { open: boolean; onClose: () => void; assetId?: string }) {
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<ReplacePhase>('idle');
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const xhrRef = useRef<XMLHttpRequest | null>(null);
  // True once step-1 has flipped the asset to "uploading" — tells the error /
  // cancel paths to terminalize the row so it never gets stuck mid-replace.
  const flippedRef = useRef(false);
  const markUploadFailed = useMutation(api.mutations.assets.markUploadFailed);
  const busy = phase === 'requesting' || phase === 'uploading' || phase === 'completing';

  // Best-effort: flip a stuck "uploading" row back to "failed". No-op on the
  // backend if a real completion already raced ahead; never throws to the UI.
  const reportFailed = useCallback(async (reason: string) => {
    if (!flippedRef.current || !assetId) return;
    try { await markUploadFailed({ assetId: assetId as any, reason }); } catch { /* cron sweep backstop */ }
    flippedRef.current = false;
  }, [assetId, markUploadFailed]);

  const reset = useCallback(() => {
    setFile(null); setPhase('idle'); setProgress(0); setError(null); setDrag(false);
  }, []);

  const close = useCallback(() => {
    // Abort an in-flight PUT so a half-uploaded object doesn't keep running.
    if (xhrRef.current) { try { xhrRef.current.abort(); } catch { /* noop */ } xhrRef.current = null; }
    reset();
    onClose();
  }, [onClose, reset]);

  const pick = useCallback((f: File | null) => {
    if (!f) return;
    if (!isSupported(f)) { setError(`${f.name}: formato no soportado`); return; }
    if (f.size > MAX_BYTES) { setError(`${f.name}: excede 10 GB`); return; }
    setError(null); setFile(f);
  }, []);

  const doReplace = useCallback(async () => {
    if (!file || !assetId) return;
    const contentType = file.type || 'application/octet-stream';
    const base = resolveHttpBase();
    try {
      // Step 1 — flip asset to "uploading" + presign.
      setPhase('requesting'); setProgress(0); setError(null);
      const r1 = await fetch(`${base}/api/upload/replace-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, filename: file.name, contentType }),
      });
      if (!r1.ok) throw new Error(`Registro rechazado (HTTP ${r1.status}): ${await r1.text()}`);
      const { uploadUrl, cloudfrontUrl } = (await r1.json()) as { uploadUrl: string; cloudfrontUrl: string };
      flippedRef.current = true; // asset is now in "uploading"; must terminalize on failure

      // Step 2 — PUT bytes to S3 with real progress.
      setPhase('uploading');
      await putWithProgress(uploadUrl, file, contentType, setProgress, (xhr) => { xhrRef.current = xhr; });
      xhrRef.current = null;

      // Step 3 — restart the transcode for this same asset id.
      setPhase('completing');
      const r3 = await fetch(`${base}/api/upload/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assetId, cloudfrontUrl }),
      });
      if (!r3.ok) throw new Error(`Finalización rechazada (HTTP ${r3.status}): ${await r3.text()}`);

      flippedRef.current = false; // completed for real — don't terminalize
      setPhase('done');
      toast({ variant: 'success', message: 'Archivo reemplazado. La conversión se reinició.' });
      reset();
      onClose();
    } catch (err) {
      xhrRef.current = null;
      const msg = err instanceof Error ? err.message : String(err);
      void reportFailed(msg); // unstick the row if step-1 already flipped it
      setPhase('error');
      setError(msg);
    }
  }, [file, assetId, toast, reset, onClose, reportFailed]);

  const label = phase === 'requesting' ? 'Registrando…'
    : phase === 'uploading' ? `Subiendo ${progress}%`
      : phase === 'completing' ? 'Finalizando…'
        : 'Reemplazar';

  return (
    <Modal open={open} onClose={close} title="Reemplazar archivo" width={480}
      footer={<>
        <Btn variant="ghost" onClick={close} disabled={busy}>Cancelar</Btn>
        <Btn variant="solid" loading={busy} disabled={!file || !assetId || busy} onClick={() => { void doReplace(); }}>{label}</Btn>
      </>}>
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--body-muted)', lineHeight: 1.5 }}>Reemplazar conserva el mismo video con todos sus metadatos, cortes y referencias en programaciones — solo cambia el archivo de origen y se vuelve a convertir.</p>
      {!assetId && <FreshnessBanner show message="No se pudo identificar el video a reemplazar." />}
      <div
        onClick={() => { if (!busy) inputRef.current?.click(); }}
        onDragOver={(e) => { e.preventDefault(); if (!busy) setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy) pick(e.dataTransfer.files?.[0] ?? null); }}
        style={{ border: `2px dashed ${drag ? 'var(--cta)' : 'var(--border-cool)'}`, borderRadius: 16, padding: '32px 16px', textAlign: 'center', cursor: busy ? 'default' : 'pointer', background: drag ? 'var(--snow)' : 'transparent' }}>
        <input ref={inputRef} type="file" accept="video/*,.mkv,.mpeg,.mpg" style={{ display: 'none' }}
          onChange={(e) => { pick(e.target.files?.[0] ?? null); e.currentTarget.value = ''; }} />
        <Icon name="uploadCloud" size={36} color="var(--muted-slate)" />
        <div style={{ fontSize: 14, marginTop: 10 }}>
          {file ? file.name : 'Arrastra el nuevo archivo aquí o haz clic para elegir'}
        </div>
        {file && <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginTop: 4 }}>{(file.size / 1024 ** 2).toFixed(1)} MB</div>}
      </div>
      {(phase === 'uploading' || phase === 'completing') && (
        <div style={{ marginTop: 14 }}>
          <Progress value={phase === 'completing' ? 100 : progress} color="var(--cta)" />
        </div>
      )}
      {error && <div style={{ marginTop: 12, fontSize: 13, color: 'var(--bad)' }}>{error}</div>}
    </Modal>
  );
}

/* ---------- 6.8 Move to trash modal ---------- */
/**
 * When `onConfirm` is provided, the modal delegates the confirm to the caller
 * (which wires the real `moveToTrash` mutation through `useMutationWithToast`,
 * so the success/error toast is owned there). Without it, the modal preserves
 * the prototype's mock-toast behaviour for surfaces not yet wired.
 */
export function TrashModal({ open, onClose, count = 1, onConfirm = null }) {
  const { toast } = useToast();
  const handleConfirm = onConfirm
    ? onConfirm
    : () => toast({ variant: 'success', message: 'Movido a la papelera.', action: { label: 'Deshacer', onClick: () => {} } });
  return (
    <ConfirmModal open={open} onClose={onClose} title="Mover a la papelera" danger typed={count >= 5 ? 'ELIMINAR' : undefined}
      body={`${count} ${count === 1 ? 'video se moverá' : 'videos se moverán'} a la papelera. Las programaciones que los referencian mostrarán huecos donde estaban.`}
      confirmLabel="Mover a la papelera" onConfirm={handleConfirm} />
  );
}

/* ---------- 7.6 Schedule Validation drawer — T316 / A5 decomposed ---------- */
export { ScheduleValidationDrawer } from '../schedule/ScheduleValidationDrawer';

/* ---------- 10.11 Version History detail modal — T316 / A6 decomposed ---------- */
export { VersionDetailModal } from '../versions/VersionDetailModal';

/* ---------- 12.9 Delivery operations — T316 / A9 decomposed ---------- */
// The wired Option-B DeliveryOps lives at `screens/ops/DeliveryOps.tsx`.
// The bundle keeps the named re-export for any indirect importer that
// still points at `../pending/_bundle`. The route table (routes.tsx:82)
// binds the legacy `screens/delivery/DeliveryOps` honest-state component;
// repointing routes is out of T316's scope — see
// circa-v3-integration-architectural-review/verification/errors.md
// "T316 / A9 decomposition target path divergence".
export { DeliveryOps } from '../ops/DeliveryOps';

/* ---------- 13.5 Embedded records + viewer preview (read-only) ---------- */
export function ViewerPreview() {
  const { toast } = useToast();
  // T318 — wired channel list. The featured channel is the first
  // running/ad_break row; honest skeleton/empty while resolving.
  const viewerRows = useChannelList();
  const featured = viewerRows?.find(c => c.backendStatus === 'running' || c.backendStatus === 'ad_break') ?? viewerRows?.[0];
  if (viewerRows === undefined) {
    return <Skeleton h={420} />;
  }
  if (featured === undefined) {
    return <EmptyState icon="tv" title="Sin canales" body="No hay canales para mostrar en este espacio de trabajo." />;
  }
  const embed = `<iframe src="https://circa.tv/embed/${featured.slug}"\n  width="640" height="360" allowfullscreen></iframe>`;
  return (
    <div>
      <PageHead title="Vista del espectador" description="Cómo se ven y suenan tus canales para la audiencia. Solo lectura — estos registros se administran en el sistema de contenido."
        actions={<Pill tone="neutral" icon="lock">Solo lectura</Pill>} />

      {/* Featured: deep-forest-green media band with player chrome + embed (was purple) */}
      <div className="circa-cols-8-4" style={{ marginBottom: 24 }}>
        <div style={{ background: 'linear-gradient(150deg, var(--forest-a), var(--forest-b))', borderRadius: 22, padding: 20 }}>
          <div style={{ position: 'relative' }}>
            <Thumb radius={14} live={featured.backendStatus === 'running'}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name="play" size={26} color="#fff" /></div>
            </Thumb>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 500, color: '#fff' }}>{featured.name}</div>
              <div className="t-mono" style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', textTransform: 'none', marginTop: 4 }}>circa.tv/v/{featured.slug}</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <Btn size="sm" variant="onDark" decorative icon="externalLink" onClick={() => toast({ variant: 'info', message: 'Abriendo reproductor público…' })}>Abrir reproductor</Btn>
              <Btn size="sm" variant="onDarkSolid" decorative icon="send">Compartir</Btn>
            </div>
          </div>
        </div>
        <Panel title="Insertar reproductor">
          <p style={{ margin: '0 0 12px', fontSize: 13, color: 'var(--body-muted)' }}>Pega este fragmento en tu sitio para insertar el canal en vivo.</p>
          <div style={{ position: 'relative' }}>
            <pre style={{ margin: 0, background: 'var(--forest-a)', color: '#e6f4ee', borderRadius: 14, padding: '16px 14px', fontFamily: 'var(--font-mono)', fontSize: 11.5, lineHeight: 1.6, overflowX: 'auto' }}>{embed}</pre>
            <div style={{ position: 'absolute', top: 10, right: 10 }}><Btn size="sm" variant="onDark" icon="copy" onClick={() => toast({ variant: 'success', message: 'Fragmento copiado.' })} /></div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[['Estado', featured.backendStatus === 'running' ? 'En vivo' : 'Fuera del aire'], ['Latencia', '—'], ['Calidad', '—']].map(([k, v]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}><span style={{ color: 'var(--muted-slate)' }}>{k}</span><span>{v}</span></div>
            ))}
          </div>
        </Panel>
      </div>

      {/* Public channels as soft-stone product cards */}
      <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginBottom: 12 }}>CANALES PÚBLICOS</div>
      <div className="circa-grid-3" style={{ marginBottom: 28 }}>
        {viewerRows.slice(0, 6).map(c => {
          const onAir = c.backendStatus === 'running' || c.backendStatus === 'ad_break';
          return (
            <div key={c._id} style={{ background: 'var(--soft-stone)', borderRadius: 22, overflow: 'hidden' }}>
              <Thumb radius={0} live={c.backendStatus === 'running'} label={onAir ? '' : 'Tarjeta de espera'} />
              <div style={{ padding: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, fontWeight: 500 }}>{c.name}</span>
                  <Pill tone={c.backendStatus === 'running' ? 'live' : c.backendStatus === 'ad_break' ? 'warn' : 'neutral'} size="sm">{c.backendStatus}</Pill>
                </div>
                <div className="t-mono" style={{ fontSize: 11, color: 'var(--slate)', textTransform: 'none', margin: '6px 0 12px' }}>circa.tv/v/{c.slug}</div>
                <div style={{ height: 1, background: 'rgba(0,0,0,0.06)', marginBottom: 12 }} />
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: 12, color: 'var(--slate)', display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icon name="eye" size={13} />—</span>
                  <Btn size="sm" variant="text" icon="externalLink">Abrir</Btn>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="circa-grid-2" style={{ marginBottom: 24 }}>
        <MockBoundary detail="Tabla de demostración — registros administrados en el sistema de contenido.">
          <Panel title="Versiones de audio" pad={0} right={<Pill tone="neutral" size="sm">Solo lectura</Pill>}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 0.7fr 90px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 11, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
              <span>Versión</span><span>Formato</span><span>Idioma</span><span>Estado</span></div>
            {[['Estéreo 256 kbps', 'AAC', 'ES', 'good'], ['Mono 96 kbps (pódcast)', 'MP3', 'ES', 'good'], ['Audiodescripción', 'AAC', 'ES', 'warn']].map((r, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 0.8fr 0.7fr 90px', padding: '13px 20px', alignItems: 'center', borderBottom: i < 2 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Icon name="mic" size={15} color="var(--muted-slate)" />{r[0]}</span>
                <Pill tone="neutral" size="sm">{r[1]}</Pill><span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>{r[2]}</span>
                <Pill tone={r[3]} size="sm">{r[3] === 'good' ? 'Disponible' : 'Procesando'}</Pill>
              </div>
            ))}
          </Panel>
        </MockBoundary>
        <MockBoundary detail="Tabla de demostración — registros administrados en el sistema de contenido.">
          <Panel title="Canales de pódcast" pad={0} right={<Pill tone="neutral" size="sm">Solo lectura</Pill>}>
            {[['Noticias en audio', 142, 'noticias-audio'], ['Documental — la serie', 28, 'documental-serie']].map((p, i) => (
              <div key={p[0]} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '15px 20px', borderBottom: i < 1 ? '1px solid var(--lightest-gray)' : 'none' }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--pale-green)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><Icon name="radio" size={18} color="var(--deep-green)" /></div>
                <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontSize: 14, fontWeight: 500 }}>{p[0]}</div><div className="t-mono" style={{ fontSize: 10, color: 'var(--muted-slate)', textTransform: 'none', marginTop: 2 }}>{p[1]} episodios · /{p[2]}</div></div>
                <Btn size="sm" variant="text" icon="externalLink">Feed RSS</Btn>
              </div>
            ))}
          </Panel>
        </MockBoundary>
      </div>

      <MockBoundary detail="Tabla de demostración — registros administrados en el sistema de contenido.">
        <Panel title="Registros incorporados" pad={0} right={<HelpDot text="Estos registros provienen del sistema de contenido (CMS). Se muestran aquí solo para inspección y no se editan desde Circa." />}>
          <div style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.6fr 1fr 110px', padding: '12px 20px', borderBottom: '1px solid var(--border-cool)', fontSize: 11, color: 'var(--muted-slate)', fontFamily: 'var(--font-mono)', textTransform: 'uppercase' }}>
            <span>Tipo</span><span>Registro</span><span>Actualizado</span><span></span></div>
          {[['Feed de canal', 'noticias-24 · guía 24 h', 'hace 3 min'], ['Versión de audio', 'ritmo-latino · estéreo', 'hace 1 h'], ['Canal de pódcast', 'noticias-audio · RSS', 'hace 2 h'], ['Feed de canal', 'documental · guía 18 h', 'hace 5 h']].map((r, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '0.9fr 1.6fr 1fr 110px', padding: '13px 20px', alignItems: 'center', borderBottom: i < 3 ? '1px solid var(--lightest-gray)' : 'none', fontSize: 14 }}>
              <Pill tone="neutral" size="sm">{r[0]}</Pill>
              <span className="t-mono" style={{ fontSize: 12, textTransform: 'none' }}>{r[1]}</span>
              <span style={{ fontSize: 13, color: 'var(--muted-slate)' }}>{r[2]}</span>
              <Btn size="sm" variant="text" icon="externalLink">Abrir</Btn>
            </div>
          ))}
          <div style={{ padding: '14px 20px', fontSize: 12, color: 'var(--muted-slate)', background: 'var(--snow)' }}>Estos registros se administran en el sistema de contenido y no se editan desde aquí.</div>
        </Panel>
      </MockBoundary>
    </div>
  );
}

/* ---------- 15.1 Analyse configure modal ---------- */
export function AnalyseConfigModal({ open, onClose }) {
  const { toast } = useToast();
  const [sens, setSens] = useState(0.3);
  const [mode, setMode] = useState('enrich');
  const [caps, setCaps] = useState(true);
  const [kf, setKf] = useState(true);
  const label = sens <= 0.15 ? 'Conservador' : sens <= 0.4 ? 'Balanceado' : sens <= 0.7 ? 'Agresivo' : 'Máximo';
  const eta = mode === 'full' ? '≈ 8–12 min' : '≈ 3–5 min';
  return (
    <Modal open={open} onClose={onClose} title="Configurar análisis" width={500}
      footer={<><span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', marginRight: 'auto', alignSelf: 'center' }}>TIEMPO ESTIMADO {eta}</span><Btn variant="ghost" onClick={onClose}>Cancelar</Btn><Btn variant="solid" decorative icon="sparkles" onClick={() => { onClose(); toast({ variant: 'success', message: 'Configuración guardada. ' + eta + '.' }); }}>Guardar y analizar</Btn></>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 500 }}>Sensibilidad de escena</span>
            <Pill tone="green" size="sm">{label} · {sens.toFixed(2)}</Pill>
          </div>
          <input type="range" min="0" max="1" step="0.05" value={sens} onChange={e => setSens(parseFloat(e.target.value))} style={{ width: '100%', accentColor: 'var(--deep-green)' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }} className="t-mono">
            {['Conservador', 'Balanceado', 'Agresivo', 'Máximo'].map(l => <span key={l} style={{ fontSize: 9, color: 'var(--muted-slate)' }}>{l}</span>)}
          </div>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: 'var(--muted-slate)' }}>Mayor sensibilidad encuentra más escenas, pero puede fragmentar transiciones sutiles.</p>
        </div>
        <Field label="Modo">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[['enrich', 'Enriquecimiento', 'Transcripción, subtítulos y vectores semánticos. Recomendado para VOD con búsqueda.'], ['full', 'Completo', 'Todo lo anterior más fotogramas clave y segmentación de audio. Recomendado antes de publicar a múltiples plataformas.']].map(([id, t, d]) => (
              <button key={id} onClick={() => setMode(id)} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', textAlign: 'left', border: `1px solid ${mode === id ? 'var(--deep-green)' : 'var(--border-cool)'}`, background: mode === id ? 'var(--pale-green)' : 'transparent', borderRadius: 14, padding: 14, cursor: 'pointer', fontFamily: 'var(--font-sans)' }}>
                <span style={{ width: 18, height: 18, borderRadius: '50%', border: `2px solid ${mode === id ? 'var(--deep-green)' : 'var(--border-cool)'}`, marginTop: 1, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{mode === id && <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--deep-green)' }} />}</span>
                <span><span style={{ fontWeight: 500, fontSize: 14, display: 'block' }}>{t}</span><span style={{ fontSize: 12, color: 'var(--muted-slate)' }}>{d}</span></span>
              </button>
            ))}
          </div>
        </Field>
        <div style={{ borderTop: '1px solid var(--lightest-gray)', paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)' }}>OPCIONES</div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div><div style={{ fontSize: 14, fontWeight: 500 }}>Generar subtítulos automáticos</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>Añade una pista WebVTT automática al video.</div></div>
            <Toggle on={caps} onChange={setCaps} />
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', opacity: mode === 'full' ? 1 : 0.5 }}>
            <div><div style={{ fontSize: 14, fontWeight: 500 }}>Extraer fotogramas clave</div><div style={{ fontSize: 12, color: 'var(--muted-slate)' }}>Toma un fotograma en el punto medio de cada escena. Solo en modo Completo.</div></div>
            <Toggle on={mode === 'full' && kf} onChange={setKf} disabled={mode !== 'full'} />
          </div>
        </div>
      </div>
    </Modal>
  );
}

